import { KeyValueDefault } from "@/main/models/common";
import { PreviousItemAnswersResponse } from "@/main/models/response";
import { getAnswerPrice, ReassignForm, ReassignPartData } from "@/main/screens/order/reassignOrder/reassignScreenUtils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { useCallback, useMemo, useTransition } from "react";
import { useFormContext, useWatch } from "react-hook-form";

interface UseReassignPartItemParams {
	index: number;
	partData: ReassignPartData;
	partTypeOptions: KeyValueDefault[];
}

export function useReassignPartItem({ index, partData, partTypeOptions }: UseReassignPartItemParams) {
	const { control, setValue, clearErrors } = useFormContext<ReassignForm>();
	const [isDirectAssignLoading, startReassignTransition] = useTransition();

	const selectedGuid = useWatch({
		control,
		name: `parts.${index}.selectedGuidQuoteSupplierItem`,
	});

	const isDirectAssignActive = useWatch({
		control,
		name: `parts.${index}.directAssign`,
		compute: (directAssign) => directAssign != null,
	});

	const suppliers = partData.itemAnswers;

	const primarySupplier = useMemo(() => suppliers.find((item) => item.isOriginalSelected) || suppliers[0], [suppliers]);

	const otherSuppliers = useMemo(
		() =>
			suppliers.filter((item) => item.guidAnsweredQuoteSupplierItem !== primarySupplier?.guidAnsweredQuoteSupplierItem),
		[suppliers, primarySupplier],
	);

	const resetSupplierItemToOriginal = useCallback(
		(supplierIdx: number) => {
			const original = partData.itemAnswers[supplierIdx];
			const matchingPartType = partTypeOptions.find((pt) => String(pt.default) === String(original.idPartType));

			setValue(
				`parts.${index}.supplierItems.${supplierIdx}.guidQuoteSupplierItem`,
				original.guidAnsweredQuoteSupplierItem,
			);
			if (matchingPartType) {
				setValue(`parts.${index}.supplierItems.${supplierIdx}.partType`, matchingPartType);
			}
			setValue(`parts.${index}.supplierItems.${supplierIdx}.deliveryDeadline`, String(original.deliveryDays));
			setValue(`parts.${index}.supplierItems.${supplierIdx}.price`, formatCurrency(getAnswerPrice(original)));
			setValue(`parts.${index}.reason`, null);
			setValue(`parts.${index}.reasonDescription`, "");
			clearErrors(`parts.${index}.supplierItems.${supplierIdx}`);
			clearErrors(`parts.${index}.reason`);
			clearErrors(`parts.${index}.reasonDescription`);
		},
		[partData, partTypeOptions, index, setValue, clearErrors],
	);

	const handleSelectSupplier = useCallback(
		(supplierItem: PreviousItemAnswersResponse) => {
			const thisGuid = supplierItem.guidAnsweredQuoteSupplierItem;
			const isDeselecting = selectedGuid === thisGuid;

			if (isDeselecting) {
				const currentIdx = partData.itemAnswers.findIndex((s) => s.guidAnsweredQuoteSupplierItem === thisGuid);
				if (currentIdx >= 0) resetSupplierItemToOriginal(currentIdx);

				setValue(`parts.${index}.selectedGuidQuoteSupplierItem`, null);
				return;
			}

			if (selectedGuid) {
				const prevIdx = partData.itemAnswers.findIndex((s) => s.guidAnsweredQuoteSupplierItem === selectedGuid);
				if (prevIdx >= 0) resetSupplierItemToOriginal(prevIdx);
			}

			setValue(`parts.${index}.selectedGuidQuoteSupplierItem`, thisGuid);
			setValue(`parts.${index}.directAssign`, null);
			setValue(`parts.${index}.reason`, null);
			setValue(`parts.${index}.reasonDescription`, null);
		},
		[selectedGuid, partData, index, setValue, resetSupplierItemToOriginal],
	);

	const handleToggleDirectAssign = useCallback(() => {
		setValue(`parts.${index}.reason`, null);
		setValue(`parts.${index}.reasonDescription`, null);
		clearErrors(`parts.${index}.reason`);
		clearErrors(`parts.${index}.reasonDescription`);

		if (isDirectAssignActive) {
			setValue(`parts.${index}.directAssign`, null);
			return;
		}

		if (selectedGuid) {
			const prevIdx = partData.itemAnswers.findIndex((s) => s.guidAnsweredQuoteSupplierItem === selectedGuid);
			if (prevIdx >= 0) resetSupplierItemToOriginal(prevIdx);
		}

		setValue(`parts.${index}.selectedGuidQuoteSupplierItem`, null);
		startReassignTransition(() => {
			setValue(`parts.${index}.directAssign`, {
				supplier: null,
				partType: null,
				deliveryDeadline: null,
				price: null,
			});
		});
	}, [
		isDirectAssignActive,
		selectedGuid,
		partData,
		index,
		setValue,
		clearErrors,
		resetSupplierItemToOriginal,
		startReassignTransition,
	]);

	return {
		selectedGuid,
		isDirectAssignActive,
		primarySupplier,
		otherSuppliers,
		suppliers,
		isDirectAssignLoading,
		handleSelectSupplier,
		handleToggleDirectAssign,
	};
}
